import React, { useState, useMemo, memo } from "react";

const VIEW_MODES = [
    { id: "table", label: "Table" },
    { id: "json", label: "JSON" },
    { id: "schema", label: "Schema" },
];

export default function DataViewer({ data, label, itemCount, duration }) {
    const [viewMode, setViewMode] = useState("table");

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
                <div className="flex gap-1">
                    {VIEW_MODES.map((mode) => (
                        <button
                            key={mode.id}
                            onClick={() => setViewMode(mode.id)}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                                viewMode === mode.id ? "bg-gray-200 text-gray-900 font-medium" : "text-gray-500 hover:text-gray-700"
                            }`}
                        >
                            {mode.label}
                        </button>
                    ))}
                </div>
                {itemCount != null && (
                    <span className="text-xs text-gray-400">
                        {itemCount} item{itemCount !== 1 ? "s" : ""}{duration ? ` · ${duration}ms` : ""}
                    </span>
                )}
            </div>
            <div className="flex-1 overflow-auto p-4">
                {!data ? (
                    <EmptyData label={label} />
                ) : viewMode === "table" ? (
                    <TableView data={data} />
                ) : viewMode === "json" ? (
                    <JsonView data={data} />
                ) : (
                    <SchemaView data={data} />
                )}
            </div>
        </div>
    );
}

const emptyMessage = <div className="text-sm text-gray-400 text-center py-8" />;

function EmptyData({ label }) {
    return (
        <div className="text-sm text-gray-400 text-center py-8">
            No {label || "data"} available. Run the workflow to see results.
        </div>
    );
}

const DataRow = memo(function DataRow({ row, keys, rowKey }) {
    return (
        <tr className="hover:bg-gray-50">
            {keys.map((k) => (
                <td key={k} className="px-2 py-1.5 border-b border-gray-100 text-gray-700 truncate max-w-[150px]">
                    {typeof row[k] === "object" ? JSON.stringify(row[k]) : String(row[k] ?? "")}
                </td>
            ))}
        </tr>
    );
});

function TableView({ data }) {
    const rows = useMemo(() => Array.isArray(data) ? data : [data], [data]);
    const keys = useMemo(() => Object.keys(rows[0] || {}), [rows]);

    if (rows.length === 0) return <EmptyData label="data" />;
    if (keys.length === 0) return <JsonView data={data} />;

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
                <thead>
                    <tr>
                        {keys.map((k) => (
                            <th key={k} className="text-left px-2 py-1.5 border-b border-gray-200 text-gray-500 font-medium bg-gray-50">{k}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <DataRow key={row.id || row._id || i} row={row} keys={keys} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function JsonView({ data }) {
    const json = useMemo(() => JSON.stringify(data, null, 2), [data]);
    return (
        <pre className="text-xs font-mono text-gray-700 whitespace-pre-wrap break-words">
            {json}
        </pre>
    );
}

function SchemaView({ data }) {
    const entries = useMemo(() => {
        const sample = Array.isArray(data) ? data[0] : data;
        if (!sample || typeof sample !== "object") return null;
        return Object.entries(sample);
    }, [data]);

    if (!entries) return <EmptyData label="schema" />;

    return (
        <div className="space-y-1">
            {entries.map(([key, val]) => (
                <div key={key} className="flex items-center gap-2 text-xs">
                    <span className="font-mono text-gray-700">{key}</span>
                    <span className="text-gray-400">{inferType(val)}</span>
                </div>
            ))}
        </div>
    );
}

function inferType(val) {
    if (val === null || val === undefined) return "null";
    if (Array.isArray(val)) return `array[${val.length}]`;
    return typeof val;
}
